/**
 * LiveRobotPositionViewer.jsx
 *
 * Real-time robot map viewer — matches the NavigationMapSection mobile component.
 *
 * Features
 * ────────
 * • PGM map rendered onto a canvas (offscreen canvas for performance)
 * • Smooth animated robot marker (Google Maps-style cyan arrow, LERP via rAF)
 * • Navigation trail — blue breadcrumb polyline while robot moves
 * • Planned-route overlay — dashed grey line from START → DESTINATION
 * • START (green) and DESTINATION (red) pin markers with labels
 * • Zoom (mouse-wheel) + pan (drag) with zoom buttons
 * • Socket events: ACTION_CURRENT_POSE · nav_progress · location_data · nav_target
 */
import React, { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { load as yamlLoad } from "js-yaml";
import PropTypes from "prop-types";
import { io } from "socket.io-client";
import CONFIG from "../config/config";
import { MAP_LOAD_ERROR_MESSAGE } from "./errorMessages";

// ── Constants ────────────────────────────────────────────────────────────────
const LERP_ALPHA = 0.12; // robot position lerp speed per frame
const MIN_TRAIL_PX = 4; // minimum screen-px distance between trail points
const MAX_TRAIL = 120; // max breadcrumb points kept in memory
const MIN_SCALE = 0.25;
const MAX_SCALE = 12;

// Info-bar colour per robot state; anything else falls back to amber.
const STATE_COLORS = { MOVING: "#10b981", REACHED: "#2563EB" };
const DEFAULT_STATE_COLOR = "#f59e0b";

// ── Module-level drawing helpers (no closure over component state) ────────────

/**
 * Draw the dashed planned-route line from a START px to a DESTINATION px.
 */
function _drawRouteLine(ctx, startPx, destPx) {
  if (!startPx || !destPx) return;
  ctx.save();
  ctx.strokeStyle = "#94a3b8";
  ctx.lineWidth = 2.5;
  ctx.setLineDash([8, 7]);
  ctx.lineCap = "round";
  ctx.beginPath();
  ctx.moveTo(startPx.x, startPx.y);
  ctx.lineTo(destPx.x, destPx.y);
  ctx.stroke();
  ctx.restore();
}

/**
 * Draw the travelled breadcrumb trail (solid cyan polyline) through a list of
 * screen-px points — the path the robot has actually covered so far.
 */
function _drawTrail(ctx, points) {
  if (!points || points.length < 2) return;
  ctx.save();
  ctx.strokeStyle = "#00b4d8";
  ctx.lineWidth = 3;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.globalAlpha = 0.85;
  ctx.beginPath();
  ctx.moveTo(points[0].x, points[0].y);
  for (let i = 1; i < points.length; i++) ctx.lineTo(points[i].x, points[i].y);
  ctx.stroke();
  ctx.restore();
}

/**
 * Draw the marker for where a discharged/stopped robot halted mid-route —
 * a warning-amber ring distinct from the live cyan robot arrow.
 */
function _drawStoppedMarker(ctx, x, y) {
  ctx.save();
  ctx.globalAlpha = 0.22;
  ctx.fillStyle = "#f59e0b";
  ctx.beginPath();
  ctx.arc(x, y, 15, 0, 2 * Math.PI);
  ctx.fill();
  ctx.globalAlpha = 1;
  ctx.strokeStyle = "#f59e0b";
  ctx.lineWidth = 3;
  ctx.setLineDash([4, 3]);
  ctx.beginPath();
  ctx.arc(x, y, 11, 0, 2 * Math.PI);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.fillStyle = "#fff";
  ctx.beginPath();
  ctx.arc(x, y, 4, 0, 2 * Math.PI);
  ctx.fill();
  ctx.font = "bold 10px Arial";
  ctx.fillStyle = "#92400e";
  ctx.textAlign = "left";
  ctx.fillText("Stopped", x + 16, y + 4);
  ctx.restore();
}

/**
 * Draw the robot's last-known position after it goes offline — the EXACT
 * same "Google Maps" nav-arrow shape _drawRobotArrow draws for a live robot
 * (chosen deliberately, per the approved mockup: "Option 2 — Faded + Grey
 * Tint" at https://claude.ai/artifact/U5FzT61vzQpYzxR4joRuBJ), just faded to
 * 55% opacity with the cyan swapped for a neutral grey, so an offline marker
 * reads as "the same robot, dimmed" rather than a differently-shaped icon.
 * The "Last Seen" callout is a HOVER tooltip (see the hit-test wiring
 * below), not a permanent label, so the marker itself stays quiet.
 */
function _drawOfflineMarker(ctx, x, y, yaw) {
  ctx.save();
  ctx.globalAlpha = 0.55;
  ctx.translate(x, y);
  ctx.rotate(Math.PI / 2 - yaw);

  // Drop shadow
  ctx.save();
  ctx.globalAlpha *= 0.18;
  ctx.fillStyle = "#000";
  ctx.beginPath();
  ctx.moveTo(0, -14);
  ctx.lineTo(10, 14);
  ctx.quadraticCurveTo(0, 7, -10, 14);
  ctx.closePath();
  ctx.fill();
  ctx.restore();

  // White outline for map contrast — same weight as the live arrow
  ctx.strokeStyle = "#fff";
  ctx.lineWidth = 3;
  ctx.lineJoin = "round";
  ctx.beginPath();
  ctx.moveTo(0, -14);
  ctx.lineTo(10, 14);
  ctx.quadraticCurveTo(0, 7, -10, 14);
  ctx.closePath();
  ctx.stroke();

  // Grey fill in place of the live arrow's cyan
  ctx.fillStyle = "#94a3b8";
  ctx.beginPath();
  ctx.moveTo(0, -14);
  ctx.lineTo(10, 14);
  ctx.quadraticCurveTo(0, 7, -10, 14);
  ctx.closePath();
  ctx.fill();

  // Static halo ring (grey, not the live arrow's cyan) — not pulsing: an
  // animated halo would visually imply "live", which an offline marker must
  // not suggest.
  ctx.strokeStyle = "rgba(148,163,184,0.35)";
  ctx.lineWidth = 2.5;
  ctx.beginPath();
  ctx.arc(0, 0, 18, 0, 2 * Math.PI);
  ctx.stroke();

  ctx.restore();
}

/**
 * Draw a coloured circle pin — matches mobile NavigationMapSection exactly.
 * Outer glow (r=16, opacity 0.18) + main ring (r=10, opacity 0.95) + white dot (r=4) + label.
 */
function _drawPin(ctx, x, y, color, label, labelColor) {
  ctx.save();
  // Outer glow
  ctx.globalAlpha = 0.18;
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(x, y, 16, 0, 2 * Math.PI);
  ctx.fill();
  // Main ring
  ctx.globalAlpha = 0.95;
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(x, y, 10, 0, 2 * Math.PI);
  ctx.fill();
  // Inner white dot
  ctx.fillStyle = "#fff";
  ctx.beginPath();
  ctx.arc(x, y, 4, 0, 2 * Math.PI);
  ctx.fill();
  // Label
  if (label) {
    ctx.globalAlpha = 0.95;
    ctx.font = "bold 10px Arial";
    ctx.fillStyle = labelColor || "#1e293b";
    ctx.textAlign = "left";
    ctx.fillText(label, x + 14, y + 4);
  }
  ctx.restore();
}

/**
 * Draw a Google Maps-style navigation arrow at (x,y) facing ROS yaw (radians).
 *
 * ROS convention: yaw=0 → East (+x), increasing counter-clockwise.
 * Canvas convention: +y is down, rotation is clockwise.
 * Conversion: rotate canvas by (π/2 − yaw) for an arrow that defaults to pointing up.
 */
function _drawRobotArrow(ctx, x, y, yaw) {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(Math.PI / 2 - yaw);

  // Drop shadow
  ctx.globalAlpha = 0.18;
  ctx.fillStyle = "#000";
  ctx.beginPath();
  ctx.moveTo(0, -14);
  ctx.lineTo(10, 14);
  ctx.quadraticCurveTo(0, 7, -10, 14);
  ctx.closePath();
  ctx.fill();
  ctx.globalAlpha = 1;

  // White outline for map contrast
  ctx.strokeStyle = "#fff";
  ctx.lineWidth = 3;
  ctx.lineJoin = "round";
  ctx.beginPath();
  ctx.moveTo(0, -14);
  ctx.lineTo(10, 14);
  ctx.quadraticCurveTo(0, 7, -10, 14);
  ctx.closePath();
  ctx.stroke();

  // Cyan fill
  ctx.fillStyle = "#00b4d8";
  ctx.beginPath();
  ctx.moveTo(0, -14);
  ctx.lineTo(10, 14);
  ctx.quadraticCurveTo(0, 7, -10, 14);
  ctx.closePath();
  ctx.fill();

  // Pulsing halo
  ctx.strokeStyle = "rgba(0,180,216,0.35)";
  ctx.lineWidth = 2.5;
  ctx.beginPath();
  ctx.arc(0, 0, 18, 0, 2 * Math.PI);
  ctx.stroke();

  ctx.restore();
}

/**
 * Format whole seconds as "M:SS" for the info-bar duration readout.
 */
function formatDuration(totalSeconds) {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

/**
 * Load an image and resolve once decoded, rejecting on failure.
 */
function _loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to decode geminiPGMData image"));
    img.src = src;
  });
}

// ── Component ─────────────────────────────────────────────────────────────────

const LiveRobotPositionViewer = ({
  mapId,
  navigationTarget,
  previewLocations = [],
  selectedSerialID = null,
  navProgress = null,
  isStopped = false,
  // Live connectivity, computed by the caller (e.g. RobotDetails.jsx already
  // tracks this via its own socket subscription — passed through rather than
  // re-derived here, since this component doesn't know the robot's identity
  // beyond its serial). When false, the live cyan arrow is replaced by the
  // dimmed "last seen" marker below, following the caller's already-scoped
  // per-map lastKnownPosition (RobotDetails.jsx only passes it through when
  // it matches the CURRENTLY assigned map, so this component never has to
  // reason about cross-map staleness itself).
  isRobotConnected = true,
  lastKnownPosition = null,
}) => {
  // ── DOM refs ────────────────────────────────────────────────────────────
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const offscreenRef = useRef(null); // static map offscreen canvas
  const isImageModeRef = useRef(false); // true when geminiPGMData PNG is active
  const geminiImgRef = useRef(null); // preloaded HTMLImageElement for PNG mode

  // ── Map data ─────────────────────────────────────────────────────────────
  const [pgmData, setPgmData] = useState(null);
  const [mapConfig, setMapConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const pgmRef = useRef(null); // mirrors pgmData  (for use inside closures)
  const cfgRef = useRef(null); // mirrors mapConfig
  const mapReadyRef = useRef(false);

  // ── Robot animated state ─────────────────────────────────────────────────
  const robotYawRef = useRef(0);
  const currentPxRef = useRef(null); // current interpolated canvas px
  const targetPxRef = useRef(null); // latest target canvas px
  const lastConfPxRef = useRef(null); // last confirmed px (from live events)

  // ── Navigation overlay ───────────────────────────────────────────────────
  const navTrailRef = useRef([]); // breadcrumb screen coords - array of x/y points
  const navRouteRef = useRef(null); // route overlay - startPx, destPx and name
  const navQueueRef = useRef([]); // buffered nav_progress before map ready
  const pendingStartRef = useRef(false);
  const navTargetPropRef = useRef(navigationTarget);
  const isRobotConnectedRef = useRef(isRobotConnected);
  const previewLocsRef = useRef(previewLocations);
  const savedLocsRef = useRef([]); // mapCoordinate waypoints from map document

  // ── rAF / redraw ─────────────────────────────────────────────────────────
  const isMountedRef = useRef(true);
  const needsRedrawRef = useRef(false);
  const drawFrameRef = useRef(null); // always holds latest drawScene impl

  // ── Zoom / pan ───────────────────────────────────────────────────────────
  const zoomRef = useRef({ scale: 1, tx: 0, ty: 0 });
  const panRef = useRef({ active: false, startX: 0, startY: 0, startTx: 0, startTy: 0 });
  // Touch state: 1-finger pan, 2-finger pinch-zoom (trackpad pinch fires as
  // wheel+ctrlKey in Chrome/Firefox/Safari, handled in handleWheel below;
  // this covers actual touchscreen pinch gestures).
  const touchRef = useRef({
    mode: null,
    lastX: 0,
    lastY: 0,
    lastDist: 0,
    lastMidX: 0,
    lastMidY: 0,
  });

  // ── UI state (triggers re-renders for the info bar) ──────────────────────
  const [robotCoords, setRobotCoords] = useState(null);
  const [robotState, setRobotState] = useState("");
  const [distanceTraveled, setDistanceTraveled] = useState(0); // metres, current leg
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  // Distance/duration accumulation for the current leg — reset whenever a new
  // route starts (navigationTarget changes) so each leg reports its own totals
  // rather than an ever-growing session sum.
  const lastWorldPosRef = useRef(null); // {x,y} in world metres
  const legStartTimeRef = useRef(null);
  const stoppedPxRef = useRef(null); // screen px where the robot halted, once isStopped

  // Screen px of the dimmed "last seen" marker, recomputed whenever
  // lastKnownPosition or the map's world<->px transform changes — read by
  // both drawOfflineMarker and the hover hit-test below.
  const offlinePxRef = useRef(null);
  // null when not hovering; otherwise the tooltip's screen position (relative
  // to wrapRef), recomputed on every qualifying mousemove so it tracks
  // pan/zoom while the cursor stays over the marker.
  const [lastSeenTooltipPos, setLastSeenTooltipPos] = useState(null);
  // Tracks the hover boolean across mousemove calls purely so the diagnostic
  // log below fires once per hover transition, not once per pixel of mouse
  // movement (mousemove fires far too often to log unconditionally).
  const wasHoveringOfflineMarkerRef = useRef(false);

  const socketRef = useRef(null);
  const selectedSerialRef = useRef(selectedSerialID);
  const BASE_URL = CONFIG.BASE_URL;

  // Keep selectedSerialRef current without re-running the socket effect
  useEffect(() => {
    selectedSerialRef.current = selectedSerialID;
  }, [selectedSerialID]);

  // Move the room subscription to the newly selected robot. The socket effect
  // below is keyed on BASE_URL only (so handlers aren't torn down on every
  // selection change), which means it cannot do this itself — without this
  // effect the socket stays in the FIRST robot's room forever and selecting a
  // different robot shows a permanently frozen map. Leaving the old room also
  // matters: otherwise the client keeps paying for a pose stream it discards.
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket || !selectedSerialID) return;

    // On the very first mount the socket may not have connected yet; the
    // `connect` handler subscribes in that case, so this is a no-op then.
    if (socket.connected) socket.emit("subscribe-robot", selectedSerialID);

    return () => {
      if (socket.connected) socket.emit("unsubscribe-robot", selectedSerialID);
    };
  }, [selectedSerialID]);

  // Clear the previous robot's marker, trail and route when the selection
  // changes, so the map never shows one robot's position under another's label.
  useEffect(() => {
    currentPxRef.current = null;
    targetPxRef.current = null;
    lastConfPxRef.current = null;
    navTrailRef.current = [];
    navRouteRef.current = null;
    needsRedrawRef.current = true;
  }, [selectedSerialID]);

  // ── Sync props / state to refs ───────────────────────────────────────────
  useEffect(() => {
    pgmRef.current = pgmData;
  }, [pgmData]);
  useEffect(() => {
    cfgRef.current = mapConfig;
  }, [mapConfig]);
  useEffect(() => {
    navTargetPropRef.current = navigationTarget;
    needsRedrawRef.current = true;
    if (navigationTarget) {
      // A new leg started — begin this leg's distance/duration count from zero
      // rather than carrying over the previous destination's totals.
      lastWorldPosRef.current = null;
      legStartTimeRef.current = Date.now();
      stoppedPxRef.current = null;
      setDistanceTraveled(0);
      setElapsedSeconds(0);
    } else {
      legStartTimeRef.current = null;
    }
  }, [navigationTarget]);

  // Tick the elapsed-time readout once per second while a leg is active.
  useEffect(() => {
    if (!navigationTarget) return;
    const id = setInterval(() => {
      if (legStartTimeRef.current) {
        setElapsedSeconds(Math.floor((Date.now() - legStartTimeRef.current) / 1000));
      }
    }, 1000);
    return () => clearInterval(id);
  }, [navigationTarget]);

  // Freeze the robot's current screen position as the "stopped here" marker
  // the moment it halts mid-route; cleared once a fresh leg starts (above).
  useEffect(() => {
    if (isStopped && currentPxRef.current) {
      stoppedPxRef.current = { ...currentPxRef.current };
      needsRedrawRef.current = true;
    } else if (!isStopped) {
      stoppedPxRef.current = null;
    }
  }, [isStopped]);

  useEffect(() => {
    previewLocsRef.current = previewLocations;
    needsRedrawRef.current = true;
  }, [previewLocations]);

  // Recompute the dimmed "last seen" marker's screen position whenever the
  // robot's connectivity flips or a new lastKnownPosition arrives. Mirrors
  // applyPendingNavTarget's pattern below: worldToPx needs pgmRef/cfgRef,
  // which aren't ready on first mount, so a pose that arrives before the map
  // is parked in pendingOfflinePosRef and flushed once pgmData loads (see
  // applyPendingOfflinePos in the pgmData-ready effect further down).
  useEffect(() => {
    isRobotConnectedRef.current = isRobotConnected;
    setLastSeenTooltipPos(null); // never carry a stale hover across a robot/connectivity change
    if (isRobotConnected || !lastKnownPosition) {
      console.log(
        `[LiveRobotViewer] offline marker cleared: isRobotConnected=${isRobotConnected} lastKnownPosition=${
          lastKnownPosition ? "present" : "null"
        }`
      );
      offlinePxRef.current = null;
      needsRedrawRef.current = true;
      return;
    }
    if (!mapReadyRef.current) {
      console.log(
        "[LiveRobotViewer] lastKnownPosition arrived before the map was ready, queued:",
        lastKnownPosition
      );
      pendingOfflinePosRef.current = lastKnownPosition;
      return;
    }
    const px = worldToPx(lastKnownPosition.x, lastKnownPosition.y);
    console.log(
      `[LiveRobotViewer] offline marker positioned at world(${lastKnownPosition.x}, ${lastKnownPosition.y}) -> canvas(${px?.x}, ${px?.y})`
    );
    // yaw travels alongside the screen px so the dimmed arrow can face the
    // robot's actual last-known heading, not a default orientation.
    offlinePxRef.current = px ? { ...px, yaw: lastKnownPosition.yaw ?? 0 } : null;
    needsRedrawRef.current = true;
    // worldToPx reads only stable refs (pgmRef/cfgRef), not a reactive value —
    // see the same justification on the navigationTarget effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRobotConnected, lastKnownPosition]);

  // ── navigationTarget prop → immediately build route overlay ──────────────
  useEffect(() => {
    if (!navigationTarget) {
      navRouteRef.current = null;
      navTrailRef.current = [];
      pendingStartRef.current = false;
      needsRedrawRef.current = true;
      return;
    }

    if (!mapReadyRef.current) {
      pendingNavTargetRef.current = navigationTarget;
      return;
    }

    const dp = worldToPx(navigationTarget.x, navigationTarget.y);
    if (!dp) return;

    const sp = lastConfPxRef.current;
    navRouteRef.current = { startPx: sp, destPx: dp, name: navigationTarget.name || "Destination" };
    navTrailRef.current = sp ? [{ ...sp }] : [];
    pendingStartRef.current = sp === null;
    needsRedrawRef.current = true;
  }, [navigationTarget]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── World (metres) → raw PGM pixel ──────────────────────────────────────
  const worldToPx = (wx, wy) => {
    const pgm = pgmRef.current;
    const cfg = cfgRef.current;
    if (!pgm || !cfg) return null;
    const res = cfg.resolution || 0.05;
    const origin = cfg.origin || [0, 0, 0];
    return {
      x: Math.max(0, Math.min((wx - origin[0]) / res, pgm.width)),
      y: Math.max(0, Math.min(pgm.height - (wy - origin[1]) / res, pgm.height)),
    };
  };

  const pendingNavTargetRef = useRef(null);
  const pendingOfflinePosRef = useRef(null);

  // ── Apply CSS transform (zoom / pan) to canvas ───────────────────────────
  const applyTransform = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const { scale, tx, ty } = zoomRef.current;
    canvas.style.transformOrigin = "0 0";
    canvas.style.transform = `translate(${tx}px, ${ty}px) scale(${scale})`;
  };

  // ── Scene draw helpers — each owns one overlay so drawScene stays flat ────
  const drawSavedWaypoints = (ctx) => {
    const savedLocs = savedLocsRef.current;
    if (!savedLocs || savedLocs.length === 0) return;
    savedLocs.forEach((loc) => {
      const name = Object.keys(loc)[0];
      const [wx, wy] = loc[name];
      const pp = worldToPx(wx, wy);
      if (!pp) return;
      // Pin only — the checkpoint's yaw stays in the map document and is still
      // sent to the robot, it is simply not drawn on the UI.
      _drawPin(ctx, pp.x, pp.y, "#ef4444", name, "#1e3a8a");
    });
  };

  // START (green) + DESTINATION (red) pins for the active route
  const drawRoutePins = (ctx) => {
    const route = navRouteRef.current;
    const startForPin = route?.startPx || currentPxRef.current;
    if (startForPin) {
      _drawPin(ctx, startForPin.x, startForPin.y, "#10b981", "", "#065f46");
    }
    if (route?.destPx) {
      _drawPin(
        ctx,
        route.destPx.x,
        route.destPx.y,
        "#ef4444",
        route.name || "Destination",
        "#991b1b"
      );
    }
  };

  // navigationTarget prop (blue pin) — skipped once an active route shows its own dest pin
  const drawTargetPin = (ctx) => {
    const nt = navTargetPropRef.current;
    if (!nt || navRouteRef.current) return;
    const np = worldToPx(nt.x, nt.y);
    if (np) _drawPin(ctx, np.x, np.y, "#2563EB", nt.name || "Target", "#1e3a5f");
  };

  // Preview locations (red pins for stops selected before navigation starts),
  // connected by a dashed line through the robot's current position so the
  // planned multi-stop route is visible before the user hits Start Navigation.
  const drawPreviewPins = (ctx) => {
    const previews = previewLocsRef.current;
    if (!previews || previews.length === 0) return;

    const previewPx = previews.map((loc) => worldToPx(loc.x, loc.y)).filter(Boolean);
    const routeStart = currentPxRef.current;
    const routePoints = routeStart ? [routeStart, ...previewPx] : previewPx;
    for (let i = 0; i < routePoints.length - 1; i++) {
      _drawRouteLine(ctx, routePoints[i], routePoints[i + 1]);
    }

    previews.forEach((loc, i) => {
      const pp = worldToPx(loc.x, loc.y);
      if (pp) _drawPin(ctx, pp.x, pp.y, "#ef4444", loc.name || `Stop ${i + 1}`, "#991b1b");
    });
  };

  const drawRobotMarker = (ctx) => {
    // Offline: the dimmed "last seen" marker replaces the live arrow entirely
    // — showing both would read as "it's here AND there", which is wrong;
    // there is exactly one marker on screen for this robot at any time.
    if (!isRobotConnectedRef.current) return;
    if (!currentPxRef.current) return;
    _drawRobotArrow(ctx, currentPxRef.current.x, currentPxRef.current.y, robotYawRef.current);
  };

  // Where the robot was last confirmed live, shown only while disconnected.
  const drawOfflineMarker = (ctx) => {
    if (isRobotConnectedRef.current) return;
    const px = offlinePxRef.current;
    if (!px) return;
    _drawOfflineMarker(ctx, px.x, px.y, px.yaw ?? 0);
  };

  // Dashed planned-route line (start → destination) beneath the solid
  // travelled-trail polyline, so the user sees both what's planned and what's
  // already been covered.
  const drawPlannedRoute = (ctx) => {
    const route = navRouteRef.current;
    if (!route) return;
    _drawRouteLine(ctx, route.startPx, route.destPx);
  };

  const drawTravelledTrail = (ctx) => {
    _drawTrail(ctx, navTrailRef.current);
  };

  // Where a discharged/paused robot halted mid-route.
  const drawStoppedMarker = (ctx) => {
    const px = stoppedPxRef.current;
    if (!px) return;
    _drawStoppedMarker(ctx, px.x, px.y);
  };

  // ── Full scene draw — reads only from refs, called by rAF loop ───────────
  const drawScene = () => {
    const canvas = canvasRef.current;
    const oc = offscreenRef.current;
    const pgm = pgmRef.current;
    if (!canvas || !oc || !pgm) return;

    if (canvas.width !== pgm.width) canvas.width = pgm.width;
    if (canvas.height !== pgm.height) canvas.height = pgm.height;

    const ctx = canvas.getContext("2d");

    ctx.drawImage(oc, 0, 0);
    drawSavedWaypoints(ctx);
    drawPlannedRoute(ctx);
    drawTravelledTrail(ctx);
    drawRoutePins(ctx);
    drawTargetPin(ctx);
    drawPreviewPins(ctx);
    drawStoppedMarker(ctx);
    drawOfflineMarker(ctx);
    drawRobotMarker(ctx);
  };

  drawFrameRef.current = drawScene;

  // ── rAF loop — runs once on mount ────────────────────────────────────────
  useEffect(() => {
    isMountedRef.current = true;
    let frame;
    const loop = () => {
      if (!isMountedRef.current) return;

      if (currentPxRef.current && targetPxRef.current) {
        const dx = targetPxRef.current.x - currentPxRef.current.x;
        const dy = targetPxRef.current.y - currentPxRef.current.y;
        if (Math.abs(dx) > 0.3 || Math.abs(dy) > 0.3) {
          currentPxRef.current = {
            x: currentPxRef.current.x + dx * LERP_ALPHA,
            y: currentPxRef.current.y + dy * LERP_ALPHA,
          };
          needsRedrawRef.current = true;
        }
      }

      if (needsRedrawRef.current) {
        drawFrameRef.current?.();
        needsRedrawRef.current = false;
      }

      frame = requestAnimationFrame(loop);
    };
    frame = requestAnimationFrame(loop);
    return () => {
      isMountedRef.current = false;
      cancelAnimationFrame(frame);
    };
  }, []);

  // ── Offscreen-canvas build helpers — keep the effect below flat ──────────
  const paintOffscreenCanvas = (w, h, pixels) => {
    const oc = document.createElement("canvas");
    oc.width = w;
    oc.height = h;
    const octx = oc.getContext("2d");

    if (isImageModeRef.current && geminiImgRef.current) {
      octx.drawImage(geminiImgRef.current, 0, 0, w, h);
    } else if (pixels) {
      const idata = octx.createImageData(w, h);
      for (let i = 0; i < pixels.length; i++) {
        idata.data[i * 4] = pixels[i];
        idata.data[i * 4 + 1] = pixels[i];
        idata.data[i * 4 + 2] = pixels[i];
        idata.data[i * 4 + 3] = 255;
      }
      octx.putImageData(idata, 0, 0);
    }
    return oc;
  };

  const fitZoomToWrap = (w, h) => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const cw = wrap.clientWidth || 800;
    const ch = wrap.clientHeight || 600;
    const fit = Math.min(cw / w, ch / h, 1);
    zoomRef.current = { scale: fit, tx: (cw - w * fit) / 2, ty: (ch - h * fit) / 2 };
    applyTransform();
  };

  const applyPendingNavTarget = () => {
    const pending = pendingNavTargetRef.current;
    if (!pending) return;
    pendingNavTargetRef.current = null;
    const dp = worldToPx(pending.x, pending.y);
    if (!dp) return;
    const sp = lastConfPxRef.current;
    navRouteRef.current = { startPx: sp, destPx: dp, name: pending.name || "Destination" };
    navTrailRef.current = sp ? [{ ...sp }] : [];
    pendingStartRef.current = sp === null;
  };

  const flushNavQueue = () => {
    navQueueRef.current.splice(0).forEach(({ x, y, theta }) => {
      const px = worldToPx(x, y);
      if (!px) return;
      targetPxRef.current = px;
      if (!currentPxRef.current) currentPxRef.current = { ...px };
      lastConfPxRef.current = px;
      if (theta !== undefined) robotYawRef.current = theta;
    });
  };

  const applyPendingOfflinePos = () => {
    const pending = pendingOfflinePosRef.current;
    if (!pending) return;
    pendingOfflinePosRef.current = null;
    const px = worldToPx(pending.x, pending.y);
    offlinePxRef.current = px ? { ...px, yaw: pending.yaw ?? 0 } : null;
    console.log(
      `[LiveRobotViewer] queued offline marker flushed now that the map is ready: world(${pending.x}, ${pending.y}) -> canvas`,
      offlinePxRef.current
    );
  };

  // ── Build offscreen canvas when map data is ready ────────────────────────
  useEffect(() => {
    if (!pgmData) {
      offscreenRef.current = null;
      mapReadyRef.current = false;
      return;
    }

    const { width, height, pixels } = pgmData;
    const w = Math.floor(Number(width));
    const h = Math.floor(Number(height));
    if (!Number.isFinite(w) || !Number.isFinite(h)) return;

    offscreenRef.current = paintOffscreenCanvas(w, h, pixels);
    mapReadyRef.current = true;

    fitZoomToWrap(w, h);
    applyPendingNavTarget();
    flushNavQueue();
    applyPendingOfflinePos();

    needsRedrawRef.current = true;
  }, [pgmData]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── PGM parser ────────────────────────────────────────────────────────────
  const parsePGM = async (pgmBlob) => {
    const ab = await pgmBlob.arrayBuffer();
    const data = new Uint8Array(ab);
    const hdr = new TextDecoder("ascii").decode(data.subarray(0, 2048));
    const lines = hdr.split(/\r?\n/).filter((l) => l.length > 0 && !l.startsWith("#"));
    if (lines[0].trim() !== "P5") throw new Error("Not a valid P5 PGM file");
    const [width, height] = lines[1].trim().split(/\s+/).map(Number);
    const maxGray = parseInt(lines[2].trim(), 10);
    if (!Number.isFinite(width) || !Number.isFinite(height) || !Number.isFinite(maxGray)) {
      throw new TypeError("Invalid PGM header");
    }
    const hlen = hdr.indexOf(lines[2]) + lines[2].length + 1;
    const raw = data.subarray(hlen, hlen + width * height);
    const pixels = Array.from(raw, (v) => (v / maxGray) * 255);
    return { width, height, pixels };
  };

  // ── Fetch map + YAML when mapId changes ──────────────────────────────────
  useEffect(() => {
    if (!mapId) {
      setPgmData(null);
      setMapConfig(null);
      return;
    }
    setLoading(true);
    setError(null);
    const token = localStorage.getItem("portalToken");
    const headers = { Authorization: `Bearer ${token}` };
    (async () => {
      try {
        const [mapRes, yamlRes] = await Promise.all([
          axios.get(`${BASE_URL}/api/maps/${mapId}`, { headers }),
          axios.get(`${BASE_URL}/api/maps/base64-to-yaml/${mapId}`, {
            responseType: "text",
            headers,
          }),
        ]);
        setMapConfig(yamlLoad(yamlRes.data));
        const mapDoc = mapRes.data;
        savedLocsRef.current = mapDoc.mapCoordinate || [];

        if (mapDoc.switchPgmData && mapDoc.geminiPGMData) {
          const pgmRes = await axios.get(`${BASE_URL}/api/maps/convertbase64ToPgm/${mapId}`, {
            responseType: "blob",
            headers,
          });
          const { width, height } = await parsePGM(pgmRes.data);

          const img = await _loadImage(`data:image/png;base64,${mapDoc.geminiPGMData}`);
          geminiImgRef.current = img;
          isImageModeRef.current = true;
          setPgmData({ width, height, pixels: null });
        } else {
          isImageModeRef.current = false;
          geminiImgRef.current = null;
          const pgmRes = await axios.get(`${BASE_URL}/api/maps/convertbase64ToPgm/${mapId}`, {
            responseType: "blob",
            headers,
          });
          setPgmData(await parsePGM(pgmRes.data));
        }
      } catch (err) {
        console.error("[LiveRobotViewer] Map load failed:", err);
        setError(MAP_LOAD_ERROR_MESSAGE);
      } finally {
        setLoading(false);
      }
    })();
  }, [mapId, BASE_URL]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Zoom / pan — wheel (non-passive) ────────────────────────────────────
  // Zoom about a fixed screen point (mx, my) by a scale factor — shared by
  // mouse wheel, trackpad pinch (ctrlKey wheel), and touch pinch.
  // Stable identity: reads and writes only zoomRef (a ref) and module-level
  // constants, so an empty dep array is correct and the handlers below can list
  // it as a dep without being torn down and re-registered on every render.
  const zoomAtPoint = useCallback((mx, my, factor) => {
    const { scale: s, tx, ty } = zoomRef.current;
    const ns = Math.max(MIN_SCALE, Math.min(MAX_SCALE, s * factor));
    zoomRef.current = { scale: ns, tx: mx - (mx - tx) * (ns / s), ty: my - (my - ty) * (ns / s) };
    applyTransform();
    // applyTransform likewise only touches canvasRef/zoomRef — no reactive deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleWheel = useCallback(
    (e) => {
      e.preventDefault();
      const wrap = wrapRef.current;
      if (!wrap) return;
      const { left, top } = wrap.getBoundingClientRect();
      const mx = e.clientX - left;
      const my = e.clientY - top;
      const dir = e.deltaY < 0 ? 1 : -1;
      // Trackpad pinch is reported by the browser as a wheel event with
      // ctrlKey=true (Chrome/Firefox/Safari convention) — finer-grained deltas
      // than a physical mouse wheel, so scale exponentially by delta magnitude
      // instead of a flat step.
      let factor;
      if (e.ctrlKey) {
        factor = Math.exp(dir * Math.min(Math.abs(e.deltaY), 40) * 0.02);
      } else {
        factor = dir > 0 ? 1.12 : 1 / 1.12;
      }
      zoomAtPoint(mx, my, factor);
    },
    [zoomAtPoint]
  );

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleWheel);
    // wrapRef.current only exists once the map wrapper div has mounted (it's
    // conditionally rendered on !loading && pgmData), so this must re-run
    // whenever that mounts/unmounts — not just once on initial render.
  }, [handleWheel, loading, pgmData]);

  // ── Zoom / pan — touch (pinch with 2 fingers, drag-pan with 1) ──────────
  const touchDist = (t0, t1) => Math.hypot(t1.clientX - t0.clientX, t1.clientY - t0.clientY);
  const touchMid = (t0, t1) => ({
    x: (t0.clientX + t1.clientX) / 2,
    y: (t0.clientY + t1.clientY) / 2,
  });

  const handleTouchStart = useCallback((e) => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    if (e.touches.length === 2) {
      const [t0, t1] = e.touches;
      const mid = touchMid(t0, t1);
      touchRef.current = {
        mode: "pinch",
        lastDist: touchDist(t0, t1),
        lastMidX: mid.x,
        lastMidY: mid.y,
      };
    } else if (e.touches.length === 1) {
      touchRef.current = { mode: "pan", lastX: e.touches[0].clientX, lastY: e.touches[0].clientY };
    }
  }, []);

  const handleTouchMove = useCallback(
    (e) => {
      const wrap = wrapRef.current;
      if (!wrap) return;
      const t = touchRef.current;

      if (e.touches.length === 2 && t.mode === "pinch") {
        e.preventDefault();
        const [t0, t1] = e.touches;
        const dist = touchDist(t0, t1);
        const mid = touchMid(t0, t1);
        const { left, top } = wrap.getBoundingClientRect();
        if (t.lastDist > 0) {
          zoomAtPoint(mid.x - left, mid.y - top, dist / t.lastDist);
        }
        touchRef.current = { ...t, lastDist: dist, lastMidX: mid.x, lastMidY: mid.y };
        return;
      }

      if (e.touches.length === 1 && t.mode === "pan") {
        e.preventDefault();
        const touch = e.touches[0];
        const dx = touch.clientX - t.lastX;
        const dy = touch.clientY - t.lastY;
        zoomRef.current.tx += dx;
        zoomRef.current.ty += dy;
        applyTransform();
        touchRef.current = { ...t, lastX: touch.clientX, lastY: touch.clientY };
      }
    },
    [zoomAtPoint]
  );

  const handleTouchEnd = useCallback((e) => {
    if (e.touches.length === 0) {
      touchRef.current = { mode: null, lastX: 0, lastY: 0, lastDist: 0, lastMidX: 0, lastMidY: 0 };
    } else if (e.touches.length === 1) {
      touchRef.current = { mode: "pan", lastX: e.touches[0].clientX, lastY: e.touches[0].clientY };
    }
  }, []);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    el.addEventListener("touchstart", handleTouchStart, { passive: true });
    el.addEventListener("touchmove", handleTouchMove, { passive: false });
    el.addEventListener("touchend", handleTouchEnd, { passive: true });
    el.addEventListener("touchcancel", handleTouchEnd, { passive: true });
    return () => {
      el.removeEventListener("touchstart", handleTouchStart);
      el.removeEventListener("touchmove", handleTouchMove);
      el.removeEventListener("touchend", handleTouchEnd);
      el.removeEventListener("touchcancel", handleTouchEnd);
    };
    // Same reasoning as the wheel effect above — re-attach whenever the
    // wrapper div (conditionally rendered) mounts/unmounts.
  }, [handleTouchStart, handleTouchMove, handleTouchEnd, loading, pgmData]);

  const handleMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    panRef.current = {
      active: true,
      startX: e.clientX,
      startY: e.clientY,
      startTx: zoomRef.current.tx,
      startTy: zoomRef.current.ty,
    };
    if (wrapRef.current) wrapRef.current.style.cursor = "grabbing";
  }, []);

  // Hit-test radius in canvas/PGM px around the offline marker's centre —
  // matches the marker's own drawn radius (15px outer ring) plus a little
  // slack so the hover target isn't pixel-perfect-only.
  const OFFLINE_MARKER_HOVER_RADIUS = 18;

  const handleMouseMove = useCallback((e) => {
    if (panRef.current.active) {
      zoomRef.current.tx = panRef.current.startTx + (e.clientX - panRef.current.startX);
      zoomRef.current.ty = panRef.current.startTy + (e.clientY - panRef.current.startY);
      applyTransform();
      return;
    }

    // Hover detection for the "Last Seen" tooltip — independent of panning,
    // so it still works on a static (non-dragging) mouse move. Only relevant
    // while offline; skip the hit-test work entirely otherwise.
    const px = offlinePxRef.current;
    if (!px || isRobotConnectedRef.current) {
      setLastSeenTooltipPos(null);
      return;
    }
    const wrap = wrapRef.current;
    if (!wrap) return;
    const rect = wrap.getBoundingClientRect();
    const { scale, tx, ty } = zoomRef.current;
    // Screen px -> canvas/PGM px, inverting the same transform applyTransform
    // puts on the canvas element (translate then scale).
    const canvasX = (e.clientX - rect.left - tx) / scale;
    const canvasY = (e.clientY - rect.top - ty) / scale;
    const isHovering = Math.hypot(canvasX - px.x, canvasY - px.y) <= OFFLINE_MARKER_HOVER_RADIUS;
    if (!isHovering) {
      if (wasHoveringOfflineMarkerRef.current) {
        console.log("[LiveRobotViewer] left offline marker hover, tooltip hidden");
        wasHoveringOfflineMarkerRef.current = false;
      }
      setLastSeenTooltipPos(null);
      return;
    }
    if (!wasHoveringOfflineMarkerRef.current) {
      console.log("[LiveRobotViewer] hovering offline marker, showing 'Last Seen' tooltip");
      wasHoveringOfflineMarkerRef.current = true;
    }
    // Marker's own screen position (canvas px -> screen px), so the tooltip
    // sits directly above the icon regardless of current zoom/pan — same
    // forward transform applyTransform puts on the canvas element.
    setLastSeenTooltipPos({ x: px.x * scale + tx, y: px.y * scale + ty });
  }, []);

  const handleMouseUp = useCallback(() => {
    panRef.current.active = false;
    if (wrapRef.current) wrapRef.current.style.cursor = "grab";
  }, []);

  const handleZoomIn = () => {
    const z = zoomRef.current;
    zoomRef.current = { ...z, scale: Math.min(MAX_SCALE, z.scale * 1.5) };
    applyTransform();
  };

  const handleZoomOut = () => {
    const z = zoomRef.current;
    zoomRef.current = { ...z, scale: Math.max(MIN_SCALE, z.scale / 1.5) };
    applyTransform();
  };

  const handleZoomReset = () => {
    const pgm = pgmRef.current;
    const wrap = wrapRef.current;
    if (!pgm || !wrap) return;
    const cw = wrap.clientWidth || 800;
    const ch = wrap.clientHeight || 600;
    const fit = Math.min(cw / pgm.width, ch / pgm.height, 1);
    zoomRef.current = {
      scale: fit,
      tx: (cw - pgm.width * fit) / 2,
      ty: (ch - pgm.height * fit) / 2,
    };
    applyTransform();
  };

  // Accumulate straight-line distance between consecutive world-frame fixes.
  // Only called while a leg is active (legStartTimeRef set), so idle jitter
  // before/after navigation never inflates the readout.
  const accumulateDistance = (wx, wy) => {
    if (!legStartTimeRef.current) return;
    const last = lastWorldPosRef.current;
    if (last) {
      const d = Math.hypot(wx - last.x, wy - last.y);
      if (d > 0.01) setDistanceTraveled((prev) => prev + d);
    }
    lastWorldPosRef.current = { x: wx, y: wy };
  };

  // ── ACTION_CURRENT_POSE helpers — keep the handler below flat ────────────
  const computeYawFromPose = (pose) => {
    if (pose.yaw !== undefined) return pose.yaw;
    const qz = pose.orientation_z;
    const qw = pose.orientation_w;
    if (qz !== undefined) return Math.atan2(2 * qw * qz, 1 - 2 * qz * qz);
    return 0;
  };

  const updateNavTrailOnPose = (px) => {
    if (pendingStartRef.current && navRouteRef.current) {
      pendingStartRef.current = false;
      navRouteRef.current = { ...navRouteRef.current, startPx: { ...px } };
      navTrailRef.current = [{ ...px }];
    }

    if (navRouteRef.current) {
      const trail = navTrailRef.current;
      const last = trail[trail.length - 1];
      if (!last || Math.hypot(px.x - last.x, px.y - last.y) > MIN_TRAIL_PX) {
        navTrailRef.current = [...trail.slice(-MAX_TRAIL), { ...px }];
      }
    }
  };

  // ── nav_progress helpers — keep the handler below flat ───────────────────
  const queuePendingNavProgress = (d, wx, wy) => {
    if (wx === undefined || wy === undefined || d.state === "reached" || d.state === "stopped") {
      return;
    }
    navQueueRef.current.push({ x: wx, y: wy, theta: d.theta, state: d.state });
  };

  const resetNavOnReachedOrStopped = () => {
    navTrailRef.current = [];
    navRouteRef.current = null;
    needsRedrawRef.current = true;
  };

  const startRouteFromNavTarget = (d) => {
    if (
      d.state !== "moving" ||
      navRouteRef.current ||
      !navTargetPropRef.current ||
      typeof d.target_x !== "number" ||
      typeof d.target_y !== "number"
    ) {
      return;
    }
    const dp = worldToPx(d.target_x, d.target_y);
    if (!dp) return;
    const sp = lastConfPxRef.current || targetPxRef.current;
    navRouteRef.current = { startPx: sp, destPx: dp, name: d.target || "Destination" };
    navTrailRef.current = sp ? [sp] : [];
  };

  const updateRobotFromProgress = (d, wx, wy) => {
    if (wx === undefined || wy === undefined) return;
    if (typeof d.theta === "number") robotYawRef.current = d.theta;
    const px = worldToPx(wx, wy);
    if (!px) return;

    targetPxRef.current = px;
    if (!currentPxRef.current) currentPxRef.current = { ...px };
    lastConfPxRef.current = px;

    const trail = navTrailRef.current;
    const last = trail[trail.length - 1];
    if (!last || Math.hypot(px.x - last.x, px.y - last.y) > MIN_TRAIL_PX) {
      navTrailRef.current = [...trail.slice(-MAX_TRAIL), px];
    }

    needsRedrawRef.current = true;
    accumulateDistance(wx, wy);
    setRobotCoords({
      x: wx,
      y: wy,
      yaw: typeof d.theta === "number" ? d.theta : robotYawRef.current,
    });
  };

  // ── Socket setup ─────────────────────────────────────────────────────────
  useEffect(() => {
    const socket = io(BASE_URL);
    socketRef.current = socket;

    socket.on("connect", () => {
      console.log("[LiveRobotViewer] Socket connected, id:", socket.id);
      // Read the robot from the `selectedSerialID` prop (via ref) rather than the
      // `selectedRobo` localStorage key, which was removed — it resolved to null
      // here, so the lookup below asked about `undefined`. Read inside the
      // handler (not once at effect setup) so a reconnect picks up the CURRENT
      // selection instead of whatever was selected when the socket was created.
      const robotId = selectedSerialRef.current;
      socket.emit("frontend:get-device", { robotId });

      // Join this robot's room. Pose events (ACTION_CURRENT_POSE / location_data
      // / nav_progress / nav_target) are emitted server-side via emitToRobotRoom,
      // i.e. ONLY to `robot-<serialID>` and the fleet room. `frontend:get-device`
      // is just a socket-id lookup and does NOT join anything, so without this
      // emit the listeners below never fire and the map stays frozen.
      // Re-emitted on every `connect` so a reconnect re-joins the room too —
      // room membership is per-connection and is lost on drop.
      if (robotId) socket.emit("subscribe-robot", robotId);
    });

    socket.on("ACTION_CURRENT_POSE", (pose) => {
      if (selectedSerialRef.current && pose.serialID && pose.serialID !== selectedSerialRef.current)
        return;
      const yaw = computeYawFromPose(pose);
      robotYawRef.current = yaw;
      const px = worldToPx(pose.x, pose.y);
      if (px) {
        targetPxRef.current = px;
        if (!currentPxRef.current) currentPxRef.current = { ...px };
        lastConfPxRef.current = px;
        updateNavTrailOnPose(px);
        needsRedrawRef.current = true;
      }
      accumulateDistance(pose.x, pose.y);
      setRobotCoords({ x: pose.x, y: pose.y, yaw });
    });

    socket.on("nav_progress", (d) => {
      console.log("[LiveRobotViewer] nav_progress received:", d);
      if (!d) return;
      if (d.state) setRobotState(d.state.toUpperCase());
      const wx = typeof d.x === "number" ? d.x : undefined;
      const wy = typeof d.y === "number" ? d.y : undefined;

      if (!mapReadyRef.current) {
        queuePendingNavProgress(d, wx, wy);
        return;
      }

      if (d.state === "reached" || d.state === "stopped") {
        resetNavOnReachedOrStopped();
        return;
      }

      startRouteFromNavTarget(d);
      updateRobotFromProgress(d, wx, wy);
    });

    socket.on("location_data", (d) => {
      if (!d || !mapReadyRef.current) return;
      if (selectedSerialRef.current && d.serialID && d.serialID !== selectedSerialRef.current)
        return;
      const { x: wx, y: wy, theta } = d;
      if (wx === undefined || wy === undefined) return;
      const px = worldToPx(wx, wy);
      if (!px) return;

      targetPxRef.current = px;
      if (!currentPxRef.current) currentPxRef.current = { ...px };
      lastConfPxRef.current = px;
      if (typeof theta === "number") robotYawRef.current = theta;

      if (pendingStartRef.current && navRouteRef.current) {
        pendingStartRef.current = false;
        navRouteRef.current = { ...navRouteRef.current, startPx: px };
        navTrailRef.current = [px];
      }

      needsRedrawRef.current = true;
      accumulateDistance(wx, wy);
    });

    socket.on("nav_target", (d) => {
      if (!d || !mapReadyRef.current) return;
      const dp = worldToPx(d.x, d.y);
      if (!dp) return;
      const sp = lastConfPxRef.current;
      navRouteRef.current = { startPx: sp, destPx: dp, name: d.name || "Destination" };
      navTrailRef.current = sp ? [sp] : [];
      pendingStartRef.current = sp === null;
      needsRedrawRef.current = true;
    });

    socket.on("disconnect", () => console.log("[LiveRobotViewer] Disconnected"));
    socket.on("error", (e) => console.error("[LiveRobotViewer] Socket error:", e));

    return () => {
      socket.disconnect();
    };
  }, [BASE_URL]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── CSS keyframes (injected once) ────────────────────────────────────────
  useEffect(() => {
    if (document.querySelector("#lrpv-kf")) return;
    const style = document.createElement("style");
    style.id = "lrpv-kf";
    style.innerHTML = `
      @keyframes lrpv-spin  { to { transform: rotate(360deg); } }
      @keyframes lrpv-pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.7;transform:scale(1.2)} }
    `;
    document.head.appendChild(style);
  }, []);

  // ── Empty / no-map state ─────────────────────────────────────────────────
  if (!mapId) {
    return (
      <div style={S.root}>
        <div style={S.centerCard}>
          <MapIcon />
          <h3 style={S.emptyTitle}>No Map Selected</h3>
          <p style={S.emptyText}>Please select a map to view the live robot position.</p>
        </div>
      </div>
    );
  }

  // ── Main render ───────────────────────────────────────────────────────────
  return (
    <div style={S.root}>
      {error && (
        <div style={S.errBanner}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {loading && (
        <div style={S.loadBox}>
          <div style={S.spinner} />
          <p style={{ color: "#64748B", margin: 0 }}>Loading map…</p>
        </div>
      )}

      {!loading && pgmData && (
        <div style={{ flex: 1, position: "relative", overflow: "hidden" }}>
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- drag-to-pan map viewport; touch panning/pinch-zoom is wired via addEventListener above, and the zoom +/-/reset buttons below cover non-pointer input */}
          <div
            ref={wrapRef}
            style={S.mapWrap}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={(e) => {
              handleMouseUp(e);
              setLastSeenTooltipPos(null);
            }}
          >
            <canvas ref={canvasRef} style={{ display: "block", imageRendering: "pixelated" }} />

            {lastSeenTooltipPos && (
              <div
                style={{
                  ...S.lastSeenTooltip,
                  left: lastSeenTooltipPos.x,
                  top: lastSeenTooltipPos.y,
                }}
              >
                Last Seen
              </div>
            )}
          </div>

          <div style={S.zoomCtrl}>
            <button style={S.zBtn} onClick={handleZoomIn} title="Zoom in">
              +
            </button>
            <button style={S.zBtn} onClick={handleZoomOut} title="Zoom out">
              −
            </button>
            <button
              style={{ ...S.zBtn, ...S.zBtnReset }}
              onClick={handleZoomReset}
              title="Fit to view"
            >
              ⟳
            </button>
          </div>

          <div style={S.legend}>
            <LegItem color="#00b4d8" label="Robot" />
            <LegItem color="#10b981" label="Start" />
            <LegItem color="#ef4444" label="Destination" />
          </div>
        </div>
      )}

      {!loading && !pgmData && !error && (
        <div style={S.loadBox}>
          <p style={{ color: "#64748B", margin: 0 }}>Loading map data…</p>
        </div>
      )}

      {robotCoords && (
        <div style={S.infoBar}>
          <InfoItem label="Status">
            <div style={S.statusRow}>
              <div style={S.statusDot} />
              <span style={S.infoVal}>Live</span>
            </div>
          </InfoItem>

          {robotState && (
            <>
              <div style={S.infoDivider} />
              <InfoItem label="State">
                <span
                  style={{
                    ...S.infoVal,
                    color: STATE_COLORS[robotState] ?? DEFAULT_STATE_COLOR,
                  }}
                >
                  {robotState}
                </span>
              </InfoItem>
            </>
          )}

          {navigationTarget && (
            <>
              <div style={S.infoDivider} />
              <InfoItem label="Distance">
                <span style={S.infoVal}>{distanceTraveled.toFixed(1)} m</span>
              </InfoItem>
              <div style={S.infoDivider} />
              <InfoItem label="Duration">
                <span style={S.infoVal}>{formatDuration(elapsedSeconds)}</span>
              </InfoItem>
              {typeof navProgress === "number" && (
                <>
                  <div style={S.infoDivider} />
                  <InfoItem label="Progress">
                    <span style={S.infoVal}>{Math.round(navProgress)}%</span>
                  </InfoItem>
                </>
              )}
            </>
          )}

          {isStopped && (
            <>
              <div style={S.infoDivider} />
              <InfoItem label="Halted">
                <span style={{ ...S.infoVal, color: "#f59e0b" }}>Stopped mid-route</span>
              </InfoItem>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ── Sub-components ────────────────────────────────────────────────────────────

const InfoItem = ({ label, children }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
    <span
      style={{
        fontSize: 10.5,
        fontWeight: 700,
        color: "#9a9aa2",
        textTransform: "uppercase",
        letterSpacing: ".06em",
      }}
    >
      {label}
    </span>
    {children}
  </div>
);

const LegItem = ({ color, label }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
    <div
      style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: color, flexShrink: 0 }}
    />
    <span style={{ fontSize: 11, color: "#64748b" }}>{label}</span>
  </div>
);

const MapIcon = () => (
  <svg
    style={{ width: 48, height: 48, color: "#93C5FD", margin: "0 auto 16px", display: "block" }}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
    />
  </svg>
);

// ── Static styles ─────────────────────────────────────────────────────────────
const S = {
  root: { width: "100%", height: "100%", display: "flex", flexDirection: "column" },
  centerCard: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#fff",
    borderRadius: 12,
    boxShadow: "0 4px 6px rgba(59,130,246,.1)",
    border: "1px solid #DBEAFE",
    padding: 20,
    textAlign: "center",
  },
  emptyTitle: { fontSize: 18, fontWeight: 600, color: "#334155", margin: "0 0 8px" },
  emptyText: { fontSize: 14, lineHeight: 1.6, color: "#64748B", margin: 0 },
  errBanner: {
    backgroundColor: "#FEF2F2",
    border: "1px solid #FECACA",
    borderRadius: 8,
    padding: "10px 14px",
    color: "#991B1B",
    marginBottom: 8,
    flexShrink: 0,
  },
  infoBar: {
    display: "flex",
    flexWrap: "wrap",
    gap: 20,
    alignItems: "center",
    padding: "12px 16px",
    background: "#ffffff",
    borderTop: "1px solid #e6e6ea",
    flexShrink: 0,
  },
  infoDivider: {
    width: 1,
    height: 26,
    background: "#e6e6ea",
    flexShrink: 0,
  },
  infoVal: { fontSize: 13.5, fontWeight: 700, color: "#1a1a1f" },
  statusRow: { display: "flex", alignItems: "center", gap: 6 },
  statusDot: {
    width: 9,
    height: 9,
    borderRadius: "50%",
    backgroundColor: "#10B981",
    boxShadow: "0 0 6px rgba(16,185,129,.6)",
    animation: "lrpv-pulse 2s infinite",
    flexShrink: 0,
  },
  loadBox: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: 40,
  },
  spinner: {
    width: 40,
    height: 40,
    border: "4px solid #BFDBFE",
    borderTop: "4px solid #2563EB",
    borderRadius: "50%",
    animation: "lrpv-spin 1s linear infinite",
    marginBottom: 16,
  },
  mapWrap: {
    position: "absolute",
    inset: 0,
    overflow: "hidden",
    backgroundColor: "#c9cfd4",
    cursor: "grab",
    touchAction: "none",
  },
  zoomCtrl: {
    position: "absolute",
    right: 12,
    top: 12,
    display: "flex",
    flexDirection: "row",
    gap: 6,
    zIndex: 10,
  },
  zBtn: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    backgroundColor: "rgba(15,23,42,.72)",
    color: "#fff",
    border: "1px solid rgba(255,255,255,.22)",
    fontSize: 20,
    fontWeight: 700,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 1,
    outline: "none",
  },
  zBtnReset: { backgroundColor: "rgba(37,99,235,.75)", fontSize: 16 },
  legend: {
    position: "absolute",
    left: 12,
    bottom: 12,
    backgroundColor: "rgba(255,255,255,.92)",
    borderRadius: 8,
    padding: "6px 10px",
    display: "flex",
    flexDirection: "column",
    gap: 5,
    boxShadow: "0 2px 8px rgba(0,0,0,.12)",
    zIndex: 10,
  },
  // Positioned by `left`/`top` set inline to the marker's own screen px
  // (see lastSeenTooltipPos); the translate here is what lifts it ABOVE the
  // icon and centres it horizontally, rather than anchoring at the icon's
  // own x/y (its top-left corner).
  lastSeenTooltip: {
    position: "absolute",
    transform: "translate(-50%, -100%) translateY(-14px)",
    backgroundColor: "rgba(15,23,42,.92)",
    color: "#fff",
    fontSize: 11,
    fontWeight: 700,
    padding: "5px 10px",
    borderRadius: 6,
    whiteSpace: "nowrap",
    pointerEvents: "none",
    zIndex: 12,
    boxShadow: "0 2px 8px rgba(0,0,0,.25)",
  },
};

LiveRobotPositionViewer.propTypes = {
  mapId: PropTypes.any,
  navigationTarget: PropTypes.object,
  previewLocations: PropTypes.array,
  selectedSerialID: PropTypes.string,
  navProgress: PropTypes.number,
  isStopped: PropTypes.bool,
  isRobotConnected: PropTypes.bool,
  lastKnownPosition: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
    yaw: PropTypes.number,
  }),
};

InfoItem.propTypes = { label: PropTypes.string, children: PropTypes.node };
LegItem.propTypes = { color: PropTypes.string, label: PropTypes.string };

export default LiveRobotPositionViewer;
